// src/components/email/EmailList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiMail, FiSearch, FiFilter, FiEdit, FiTrash2,
  FiEye, FiClock, FiCheck, FiX, FiPaperclip, FiSend,
  FiRefreshCw, FiDownload, FiArchive, FiStar, FiChevronDown
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { emailService } from '../../utils/emailUtils';
import EmailComposer from './EmailComposer';

// Helper component for displaying recipients
const RecipientsDisplay = ({ recipients }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Normalize recipients to array of objects or strings
  const recipientList = Array.isArray(recipients) ? recipients : [recipients];

  // Helper to extract email string from object or string
  const getEmail = (r) => {
    if (!r) return '';
    if (typeof r === 'string') return r;
    return r.email || r.name || 'Unknown';
  };

  if (!recipientList || recipientList.length === 0) {
    return <span className="text-gray-400">No recipients</span>;
  }

  const displayCount = 2; // Show top 2 by default
  const hasMore = recipientList.length > displayCount;
  const shownRecipients = isExpanded ? recipientList : recipientList.slice(0, displayCount);

  return (
    <div className="flex flex-col items-start">
      <div className="flex flex-wrap gap-1">
        {shownRecipients.map((r, idx) => (
          <span key={idx} className="bg-slate-100 px-2 py-0.5 rounded text-xs text-slate-700 border border-slate-200 truncate max-w-[200px]" title={getEmail(r)}>
            {getEmail(r)}
          </span>
        ))}
      </div>

      {hasMore && (
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click
            setIsExpanded(!isExpanded);
          }}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-1 flex items-center gap-1"
        >
          {isExpanded ? (
            <>Show Less <FiChevronDown className="rotate-180" /></>
          ) : (
            <>+{recipientList.length - displayCount} more <FiChevronDown /></>
          )}
        </button>
      )}
    </div>
  );
};



const EmailList = ({ onCompose }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const organizationId = userProfile?.organizationId;
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [showComposer, setShowComposer] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [emailsPerPage] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState(null);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  // Fetch emails using the emailService
  const fetchEmails = useCallback(async (reset = false) => {
    if (!organizationId) return;

    try {
      setLoading(true);
      setError(null);

      const options = {
        limitCount: emailsPerPage,
        lastDoc: reset ? null : lastDoc,
        filters: {
          ...(selectedStatus !== 'all' && { status: selectedStatus })
        }
      };

      const result = await emailService.getEmailHistory(organizationId, options);

      console.log('Fetched emails:', result.emails);

      if (reset) {
        setEmails(result.emails);
        setCurrentPage(1);
      } else {
        setEmails(prev => [...prev, ...result.emails]);
      }

      setHasMore(result.hasMore);
      setLastDoc(result.lastDoc);

    } catch (err) {
      setError('Failed to load emails: ' + err.message);
      console.error('Error fetching emails:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId, selectedStatus, emailsPerPage, lastDoc]);

  useEffect(() => {
    // Check for success message from navigation
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message after showing it
      setTimeout(() => setSuccessMessage(null), 5000);
      // Clear the state to prevent showing again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  // Initial load and when filters change
  useEffect(() => {
    if (organizationId) {
      fetchEmails(true); // Reset to first page
    }
  }, [organizationId, selectedStatus]);

  // Handle refresh
  const handleRefresh = () => {
    setLastDoc(null);
    fetchEmails(true);
  };

  // Handle email sent from composer
  const handleEmailSent = (emailResult) => {
    const newEmail = {
      id: emailResult.emailId || Date.now(),
      to: emailResult.to || [],
      cc: emailResult.cc || [],
      bcc: emailResult.bcc || [],
      subject: emailResult.subject || '',
      htmlContent: emailResult.htmlBody || emailResult.htmlContent || '',
      attachments: emailResult.storedAttachments || [], // Use stored attachment URLs
      sentAt: new Date().toISOString(),
      status: 'sent',
      opens: 0,
      clicks: 0,
      messageId: emailResult.messageId
    };

    const updatedEmails = [newEmail, ...emails];
    setEmails(updatedEmails);
    localStorage.setItem(`emails-${organizationId}`, JSON.stringify(updatedEmails));
    setShowComposer(false);
  };

  // Filter emails locally (for search)
  const filteredEmails = emails.filter(email => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      email.subject?.toLowerCase().includes(searchLower) ||
      email.to?.some(recipient =>
        recipient.toLowerCase().includes(searchLower)
      ) ||
      (email.cc && email.cc.some(recipient =>
        recipient.toLowerCase().includes(searchLower)
      ))
    );
  });

  // Pagination for filtered results
  const totalPages = Math.ceil(filteredEmails.length / emailsPerPage);
  const startIndex = (currentPage - 1) * emailsPerPage;
  const paginatedEmails = filteredEmails.slice(startIndex, startIndex + emailsPerPage);

  const handleSelectEmail = (emailId) => {
    setSelectedEmails(prev =>
      prev.includes(emailId)
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId]
    );
  };

  const handleSelectAll = () => {
    if (selectedEmails.length === paginatedEmails.length) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(paginatedEmails.map(email => email.id));
    }
  };

  const handleBulkAction = async (action) => {
    try {
      setLoading(true);

      switch (action) {
        case 'delete':
          // In a real app, this would call a delete function
          // For now, just refresh the list
          handleRefresh();
          break;
        case 'archive':
          // Archive selected emails
          // Implementation would go here
          break;
        default:
          break;
      }

      setSelectedEmails([]);
    } catch (err) {
      setError('Failed to perform bulk action');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'text-green-600 bg-green-100';
      case 'delivered': return 'text-blue-600 bg-blue-100';
      case 'opened': return 'text-purple-600 bg-purple-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'scheduled': return 'text-yellow-600 bg-yellow-100';
      case 'draft': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleForwardEmail = async (email) => {
    try {
      setLoading(true);

      // Download attachments for forwarding
      const forwardAttachments = await emailService.prepareAttachmentsForEdit(email.attachments || []);

      const forwardedContent = `
        <div style="margin: 20px 0; padding: 20px; border-left: 3px solid #ccc; background-color: #f9f9f9;">
          <p><strong>---------- Forwarded message ----------</strong></p>
          <p><strong>From:</strong> ${userProfile?.organizationName || 'Your Organization'}</p>
          <p><strong>Date:</strong> ${new Date(email.sentAt).toLocaleString()}</p>
          <p><strong>Subject:</strong> ${email.subject}</p>
          <p><strong>To:</strong> ${email.to?.map(r => r.email).join(', ')}</p>
          ${email.attachments?.length > 0 ?
          `<p><strong>Attachments:</strong> ${email.attachments.map(a => a.name).join(', ')}</p>` :
          ''
        }
          <br/>
          ${email.htmlContent || email.htmlBody || 'No content'}
        </div>
      `;

      const forwardData = {
        to: [{ email: '', name: '' }],
        cc: [],
        bcc: [],
        subject: `Fwd: ${email.subject}`,
        htmlContent: forwardedContent,
        attachments: forwardAttachments, // Include downloaded attachments
        originalEmailId: email.id
      };

      setSelectedEmail(null);

      if (onCompose) {
        onCompose(forwardData, 'forward');
      } else {
        navigate('/email/compose', {
          state: {
            emailData: forwardData,
            mode: 'forward'
          }
        });
      }
    } catch (error) {
      setError('Failed to prepare email for forwarding: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditEmail = async (email) => {
    try {
      setLoading(true);

      // Download attachments for editing
      const editAttachments = await emailService.prepareAttachmentsForEdit(email.attachments || []);

      const editData = {
        to: email.to || [{ email: '', name: '' }],
        cc: email.cc || [],
        bcc: email.bcc || [],
        subject: email.subject,
        htmlContent: email.htmlContent || email.htmlBody,
        attachments: editAttachments, // Include downloaded attachments
        originalEmailId: email.id
      };

      setSelectedEmail(null);

      if (onCompose) {
        onCompose(editData, 'edit');
      } else {
        navigate('/email/compose', {
          state: {
            emailData: editData,
            mode: 'edit'
          }
        });
      }
    } catch (error) {
      setError('Failed to prepare email for editing: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';

    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Load more emails (for pagination)
  const loadMore = () => {
    if (hasMore && !loading) {
      fetchEmails(false);
    }
  };

  const handleComposeClick = () => {
    if (onCompose) {
      onCompose(null, 'compose');
    } else {
      setShowComposer(true);
    }
  };



  if (loading && emails.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Email History</h1>
          <button
            onClick={handleComposeClick}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <FiMail className="mr-2" size={16} />
            Compose Email
          </button>
        </div>

        <div className="animate-pulse space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Email History</h1>
        <button
          onClick={handleComposeClick}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        >
          <FiMail className="mr-2" size={16} />
          Compose Email
        </button>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center">
          <FiCheck className="mr-2" size={16} />
          {successMessage}
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center">
          <FiX className="mr-2" size={16} />
          {error}
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search emails..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="opened">Opened</option>
              <option value="failed">Failed</option>
              <option value="scheduled">Scheduled</option>
              <option value="draft">Draft</option>
            </select>

            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md disabled:opacity-50"
            >
              <FiRefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedEmails.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-800">
              {selectedEmails.length} email{selectedEmails.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkAction('archive')}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Archive
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {paginatedEmails.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <FiMail className="text-gray-400" size={24} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No emails found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || selectedStatus !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Start by composing your first email'}
            </p>
            <button
              onClick={handleComposeClick}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Compose Email
            </button>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {/* Header */}
              <div className="px-6 py-3 bg-gray-50 flex items-center">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedEmails.length === paginatedEmails.length && paginatedEmails.length > 0}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-6 flex-1 grid grid-cols-12 gap-4 text-sm font-medium text-gray-500">
                  <div className="col-span-4">Recipients</div>
                  <div className="col-span-4">Subject</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2">Date</div>
                </div>
              </div>

              {/* Email Rows */}
              {paginatedEmails.map((email) => (
                <div key={email.id} className="px-6 py-4 hover:bg-gray-50 flex items-center">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedEmails.includes(email.id)}
                      onChange={() => handleSelectEmail(email.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>

                  <div className="ml-6 flex-1 grid grid-cols-12 gap-4 items-center">

                    <div className="col-span-4">
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        <RecipientsDisplay recipients={email.to} />
                      </div>
                      {email.cc && (Array.isArray(email.cc) ? email.cc.length > 0 : email.cc) && (
                        <div className="text-xs text-gray-500 flex items-start mt-1">
                          <span className="mr-1 mt-0.5">CC:</span>
                          <RecipientsDisplay recipients={email.cc} />
                        </div>
                      )}
                    </div>

                    <div className="col-span-4">
                      <div className="text-sm text-gray-900 truncate">
                        {email.subject || 'No subject'}
                      </div>
                      <div className="flex items-center mt-1 space-x-2">
                        {email.attachments && email.attachments.length > 0 && (
                          <FiPaperclip className="text-gray-400" size={12} />
                        )}
                        {email.templateId && (
                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                            Template
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="col-span-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(email.status)}`}>
                        {email.status}
                      </span>
                    </div>

                    <div className="col-span-2">
                      <div className="text-sm text-gray-500">
                        {formatDate(email.sentAt)}
                      </div>
                    </div>
                  </div>



                  <div className="ml-4 flex items-center space-x-2">
                    <button
                      onClick={() => setSelectedEmail(email)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      title="View Email"
                    >
                      <FiEye size={16} />
                    </button>
                    <button
                      onClick={() => handleForwardEmail(email)}
                      disabled={attachmentLoading}
                      className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-50"
                      title="Forward Email"
                    >
                      {attachmentLoading ? <FiRefreshCw className="animate-spin" size={16} /> : <FiSend size={16} />}
                    </button>
                    <button
                      onClick={() => handleEditEmail(email)}
                      className="p-1 text-gray-400 hover:text-green-600"
                      title="Edit & Resend"
                    >
                      <FiEdit size={16} />
                    </button>
                    <button
                      className="p-1 text-gray-400 hover:text-gray-600"
                      title="Archive"
                    >
                      <FiArchive size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More / Pagination */}
            {(hasMore || totalPages > 1) && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {startIndex + 1} to {Math.min(startIndex + emailsPerPage, filteredEmails.length)} of {filteredEmails.length} emails
                  {searchTerm && ` (filtered from ${emails.length} total)`}
                </div>
                <div className="flex items-center space-x-2">
                  {/* Local pagination for filtered results */}
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>

                  {/* Load more from Firestore */}
                  {hasMore && !searchTerm && (
                    <button
                      onClick={loadMore}
                      disabled={loading}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loading ? 'Loading...' : 'Load More'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Email Composer Modal */}
      {showComposer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[95vh] flex flex-col overflow-hidden mx-4">
            <EmailComposer
              onClose={() => setShowComposer(false)}
              onSent={handleEmailSent}
            />
          </div>
        </div>
      )}

      {/* Email Detail Modal */}
      {selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl h-[80vh] flex flex-col overflow-hidden mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold">Email Details</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleForwardEmail(selectedEmail)}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                >
                  <FiSend className="mr-1" size={14} />
                  Forward
                </button>
                <button
                  onClick={() => handleEditEmail(selectedEmail)}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
                >
                  <FiEdit className="mr-1" size={14} />
                  Edit & Resend
                </button>
                <button
                  onClick={() => setSelectedEmail(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <FiX size={18} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-6">
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500">Subject</div>
                  <div className="font-medium">{selectedEmail.subject}</div>
                </div>

                <div>
                  <div className="text-sm text-gray-500">Recipients</div>
                  <div>{Array.isArray(selectedEmail.to) ? selectedEmail.to.join(', ') : selectedEmail.to}</div>
                </div>

                <div>
                  <div className="text-sm text-gray-500">Sent</div>
                  <div>{selectedEmail.sentAt ? new Date(selectedEmail.sentAt).toLocaleString() : 'Unknown'}</div>
                </div>

                <div>
                  <div className="text-sm text-gray-500">Status</div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedEmail.status)}`}>
                    {selectedEmail.status}
                  </span>
                </div>

                <div className="border-t pt-4">
                  <div className="text-sm text-gray-500 mb-2">Content</div>
                  <div
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: selectedEmail.htmlBody || selectedEmail.htmlContent || 'No content' }}
                  />
                </div>
              </div>
              {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                <div className="border-t pt-4">
                  <div className="text-sm text-gray-500 mb-2">Attachments ({selectedEmail.attachments.length})</div>
                  <div className="space-y-2">
                    {selectedEmail.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <div className="flex items-center text-gray-600">
                          <FiPaperclip size={14} className="mr-2" />
                          <span>{attachment.name}</span>
                          {attachment.size && (
                            <span className="ml-2 text-gray-400">
                              ({(attachment.size / 1024).toFixed(1)} KB)
                            </span>
                          )}
                        </div>
                        {attachment.url && (
                          <a
                            href={attachment.url}
                            download={attachment.name}
                            className="text-blue-600 hover:text-blue-800 text-xs flex items-center"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FiDownload size={12} className="mr-1" />
                            Download
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailList;